import { ExternalLink, Phone, Reply } from 'lucide-react';
import { renderWhatsAppText } from '../lib/whatsapp-format';
import type { ChatMessage, TemplatePreviewData } from '../types';
import { MediaContent } from './MediaContent';

interface Props {
  message: ChatMessage;
  preview: TemplatePreviewData;
}

/**
 * Renderiza um template HSM (mensagem de campanha) no thread IGUAL o cliente vê
 * no WhatsApp: header de mídia (imagem/vídeo/documento) ou de texto (negrito),
 * corpo, rodapé em cinza menor e botões como linhas com divisória no topo.
 *
 * A mídia do header vem por `message.media_url` (URL HMAC do proxy — não precisa
 * de ticket, então funciona pra campanha que tem ticket_id NULL).
 */
export function TemplatePreview({ message, preview }: Props): JSX.Element {
  const { headerText, headerMedia, body, footer, buttons } = preview;

  return (
    <div className="flex flex-col gap-1.5">
      {/* Header de mídia — reusa o MESMO componente das mensagens outbound
          (MediaContent), então tamanho/comportamento (lightbox, player, card de
          documento) ficam IDÊNTICOS ao resto do sistema. Passa a mensagem com
          `type` = formato do header (image/video/document). getMediaUrl funciona
          sem ticket (campanha tem ticket_id NULL). */}
      {headerMedia && message.media_id && (
        <MediaContent message={{ ...message, type: headerMedia }} />
      )}

      {/* Header de texto (negrito) */}
      {headerText && <div className="font-semibold text-n-900">{renderWhatsAppText(headerText)}</div>}

      {/* Corpo */}
      {body && <span className="whitespace-pre-wrap">{renderWhatsAppText(body)}</span>}

      {/* Rodapé — cinza, menor (paridade WhatsApp) */}
      {footer && <div className="text-[12px] text-n-500">{footer}</div>}

      {/* Botões — linhas com divisória no topo, texto centralizado em brand */}
      {buttons && buttons.length > 0 && (
        <div className="-mx-3 mt-1 border-n-200/70 border-t">
          {buttons.map((b, i) => {
            const Icon =
              b.type === 'url' ? ExternalLink : b.type === 'phone_number' ? Phone : Reply;
            const content = (
              <span className="flex items-center justify-center gap-1.5 px-3 py-2 font-medium text-[13px] text-brand-600">
                <Icon size={15} strokeWidth={2} />
                {b.text}
              </span>
            );
            const key = `${b.type}-${i}`;
            return b.type === 'url' && b.url ? (
              <a
                key={key}
                href={b.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block border-n-200/70 transition hover:bg-brand-50/60 [&:not(:first-child)]:border-t"
              >
                {content}
              </a>
            ) : (
              <div
                key={key}
                className="block border-n-200/70 [&:not(:first-child)]:border-t"
              >
                {content}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
